/* eslint-disable react-hooks/rules-of-hooks */
import { createSlice } from '@reduxjs/toolkit';
import { useAuth } from 'hooks';
import { API } from 'api';
import { getUIVersion } from 'utils/router-static-data';

export const DashboardSlice = createSlice({
	name: 'Dashboard',
	initialState: {},
	reducers: {},
});

const Dashboard_URL = `${process.env.REACT_APP_FAST_API_URL}`;
const userInfo = useAuth(); // get the latest token
const token = userInfo?.access_token;
const options = {
	method: 'post',
	headers: {
		'Content-Type': 'application/json',
		Accept: 'application/json',
		Authorization: `Bearer ${token}`,
	},
};

const toSafeNumber = (value, fallback = 0) => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeUnclosedTrips = (unclosed = {}) => {
	const rows = Array.isArray(unclosed?.unclosed_trips_data) ? unclosed.unclosed_trips_data : [];
	return {
		unclosed_trips_count: toSafeNumber(unclosed?.unclosed_trips_count, rows.length),
		unclosed_trips_data: rows.map((item) => ({
			...item,
			duration: toSafeNumber(item?.duration, 0),
		})),
	};
};

const parseDashboardV2Response = (response) => {
	if (response && typeof response === 'object') {
		return response;
	}

	if (typeof response !== 'string') {
		return {};
	}

	const raw = response.trim();
	const candidates = [raw];

	// Some integrations send JSON as a quoted string; handle that too.
	if ((raw.startsWith('"') && raw.endsWith('"')) || (raw.startsWith("'") && raw.endsWith("'"))) {
		candidates.push(raw.slice(1, -1).replace(/\\"/g, '"'));
	}

	for (let i = 0; i < candidates.length; i += 1) {
		try {
			const sanitized = candidates[i].replace(/\bNaN\b/g, 'null');
			return JSON.parse(sanitized);
		} catch (error) {
			// Try next candidate
		}
	}

	return {};
};

const normalizeDashboardResponseV2 = (response = {}) => {
	const parsedResponse = parseDashboardV2Response(response);
	const data = parsedResponse?.data || parsedResponse || {};

	return {
		average: toSafeNumber(data?.average, 0),
		cities: {
			cities: Array.isArray(data?.cities?.cities) ? data.cities.cities : [],
			total: toSafeNumber(data?.cities?.total, 0),
		},
		completed: {
			total_trips: toSafeNumber(data?.completed?.total_trips, 0),
			completed_trips: toSafeNumber(data?.completed?.completed_trips, 0),
			total_estimated_cost: toSafeNumber(data?.completed?.total_estimated_cost, 0),
			regular_trips_summary: {
				total: toSafeNumber(data?.completed?.regular_trips_summary?.total, 0),
				completed: toSafeNumber(data?.completed?.regular_trips_summary?.completed, 0),
				estimated_cost: toSafeNumber(
					data?.completed?.regular_trips_summary?.estimated_cost,
					0
				),
			},
			adhoc_trips_summary: {
				total: toSafeNumber(data?.completed?.adhoc_trips_summary?.total, 0),
				completed: toSafeNumber(data?.completed?.adhoc_trips_summary?.completed, 0),
				estimated_cost: toSafeNumber(
					data?.completed?.adhoc_trips_summary?.estimated_cost,
					0
				),
			},
		},
		project_count: {
			total_projects: toSafeNumber(data?.project_count?.total_projects, 0),
			total_cities: toSafeNumber(data?.project_count?.total_cities, 0),
		},
		trend: Array.isArray(data?.trend) ? data.trend : [],
		unclosed: normalizeUnclosedTrips(data?.unclosed),
		vehicles: {
			total: toSafeNumber(data?.vehicles?.total, 0),
			vehicles: Array.isArray(data?.vehicles?.vehicles) ? data.vehicles.vehicles : [],
		},
	};
};

// Trips Completed
export const getTripCompleted = (params) => async () => {
	const finalOptions = { ...options, body: JSON.stringify(params) };
	return fetch(`${Dashboard_URL}/trips/completed`, finalOptions).then(async (response) =>
		response.json()
	);
};

// Unclosed Trips
export const getUnClosed = (params) => async () => {
	const finalOptions = { ...options, body: JSON.stringify(params) };
	return fetch(`${Dashboard_URL}/trips/unclosed`, finalOptions).then(async (response) =>
		response.json()
	);
};

// Get Unclosed
export const getInterCityTrips = (params) => async () => {
	const finalOptions = { ...options, body: JSON.stringify(params) };
	return fetch(`${Dashboard_URL}/trips/cities`, finalOptions).then(async (response) =>
		response.json()
	);
};

// Get Regular Trip Trend
export const getRegularTripTrend = (params) => async () => {
	const finalOptions = { ...options, body: JSON.stringify(params) };
	return fetch(`${Dashboard_URL}/trips/trend`, finalOptions).then(async (response) =>
		response.json()
	);
};

// Get Average Trips per day
export const getAverageTripsPerDay = (params) => async () => {
	const finalOptions = { ...options, body: JSON.stringify(params) };
	return fetch(`${Dashboard_URL}/trips/average`, finalOptions).then(async (response) =>
		response.json()
	);
};

// Get Total Vehicles
export const getTotalVehicles = (params) => async () => {
	const finalOptions = { ...options, body: JSON.stringify(params) };
	return fetch(`${Dashboard_URL}/trips/vehicles`, finalOptions).then(async (response) =>
		response.json()
	);
};

// Get Total Projects Created & Total City
export const getTotalProjectsCity = (params) => async () => {
	const finalOptions = { ...options, body: JSON.stringify(params) };
	return fetch(`${Dashboard_URL}/trips/projects_count`, finalOptions).then(async (response) =>
		response.json()
	);
};

export const getDashboard =
	({ dashboard_type = '', currentPage, ...rest }) =>
	async () => {
		const isV2UI = getUIVersion() === 'new';
		let CREDENTIALS = {};
		if (isV2UI) {
			CREDENTIALS = {
				url: '/dashboard',
				data: {
					dashboard_type,
					...rest,
				},
				version: 'v2',
			};

			return API.common(CREDENTIALS).then((response) =>
				normalizeDashboardResponseV2(response)
			);
		}

		CREDENTIALS = {
			url: '/dashboard',
			data: {
				dashboard_type,
				...rest,
			},
		};

		return API.common(CREDENTIALS).then((response) => response);
	};

export default DashboardSlice.reducer;
